import { useNavigate } from 'react-router-dom'
import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  Wrap,
  WrapItem,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import { AssetCatalogShell } from '@/components/assetCatalog/AssetCatalogShell'
import { useClientContextStore } from '@/store/clientContext'
import { useAssetCatalogPaths } from '@/hooks/useAssetCatalogPaths'
import { useAssetCatalogCsvActions } from '@/hooks/useAssetCatalogCsvActions'

type TabValue = 'all' | 'trash'

const API_BASE = '/master-data/brands'

export default function BrandsPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const { path } = useAssetCatalogPaths()
  const navigate = useNavigate()
  const {
    fileInputRef,
    isExporting,
    isImporting,
    handleDownloadTemplate,
    handleFileUpload,
    handleExportData,
  } = useAssetCatalogCsvActions({
    module: 'brands',
    apiBase: API_BASE,
    tableRef,
    showError,
    showSuccess,
    showWarning,
    resourceLabel: 'brands',
  })

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        name: { name: 'Name', withSort: true, maxWidth: '180px' },
        categories: { name: 'Categories', withSort: false, maxWidth: '260px' },
        descriptions: { name: 'Description', withSort: false, maxWidth: '240px' },
        active: {
          name: 'Status',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'green', false: 'gray' },
          badgeLabels: { true: 'Active', false: 'Inactive' },
        },
        updated_at: { name: 'Updated At', withSort: true, maxWidth: '160px' },
      },
      dataSource: activeTab === 'trash' ? `${API_BASE}/archived` : API_BASE,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: activeClientCode ? 'No brands found' : 'Select a client to load data',
      hideSelection: false,
      otherActions: [],
    }),
    [activeTab, activeClientCode]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      trash: activeTab === 'trash' ? 1 : 0,
      _client: activeClientCode ?? '',
    }),
    [searchQuery, activeTab, activeClientCode]
  )

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!activeClientCode) {
      showWarning('Select a client first')
      return
    }
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these brands. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      for (const id of selectedIds) {
        if (bulkAction === 'restore') {
          await apiClient.patch(`${API_BASE}/restore/${id}`)
        } else if (bulkAction === 'delete' && activeTab === 'trash') {
          await apiClient.delete(`${API_BASE}/force-delete/${id}`)
        } else if (bulkAction === 'delete' && activeTab === 'all') {
          await apiClient.delete(`${API_BASE}/${id}`)
        }
      }
      showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} items updated` : 'Updated successfully')
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const customRenderers = useMemo(
    () => ({
      categories: (value: unknown) => {
        let names: string[] = []
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value
          if (Array.isArray(parsed)) {
            names = parsed.map((c: { name?: string }) => c?.name).filter(Boolean) as string[]
          }
        } catch {
          names = []
        }
        if (names.length === 0) return <Text as="span" color="gray.400">?</Text>
        return (
          <Wrap spacing={1}>
            {names.map((n) => (
              <WrapItem key={n}>
                <Badge fontSize="0.7em" colorScheme="purple">{n}</Badge>
              </WrapItem>
            ))}
          </Wrap>
        )
      },
      updated_at: (value: string | null | undefined) => {
        if (!value) return '?'
        try {
          return new Date(value).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        } catch {
          return String(value)
        }
      },
    }),
    []
  )

  return (
    <AssetCatalogShell>
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0} h="100%">
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>Brands</Heading>
            <Text fontSize="sm" color={colors.text}>
              Equipment brands used when adding store assets (e.g. Epson, HP)
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    <Tab>
                      All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <Box pb={2}>
                  <ActionButtons
                    pathname={path('brands')}
                    createRoute={path('brands/create')}
                    createLabel="Create Brand"
                    onExport={handleExportData}
                    onDownloadTemplate={handleDownloadTemplate}
                    isExporting={isExporting}
                    isImporting={isImporting}
                    fileInputRef={fileInputRef}
                    onFileUpload={handleFileUpload}
                  />
                </Box>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              {access.can_delete && (
                <HStack spacing={2} minW="250px">
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                    isDisabled={!activeClientCode}
                  >
                    {activeTab === 'trash' && <option value="restore">Restore</option>}
                    {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                    {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                  </Select>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleBulkActionClick}
                    isLoading={isBulkProcessing}
                    isDisabled={!activeClientCode}
                  >
                    Apply
                  </Button>
                </HStack>
              )}
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                    isDisabled={!activeClientCode}
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                      isDisabled={!activeClientCode}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              {activeClientCode ? (
                <DataTable
                  key={activeClientCode + activeTab}
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  setSubSub={setSubSub}
                  onOpenModal={(id) => navigate(path('brands/' + id))}
                  access={permissions}
                  customRenderers={customRenderers}
                />
              ) : (
                <Box py={12} textAlign="center" color={colors.text}>
                  <Text>Select a client to load data.</Text>
                </Box>
              )}
            </Box>
          </CardBody>
        </Card>
      </VStack>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>{modalMessage}</ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AssetCatalogShell>
  )
}
